import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, errorMessage } from '../../context/AuthContext';

// Création / modification d'un paquet de cartes de révision et de ses cartes.
// Les images et vidéos sont envoyées dès qu'on les choisit ; le paquet est enregistré avec « enregistrer ».

const VIDEO_MAX_MB = 50;
const IMAGE_MAX_MB = 10;
const MAX_TEXT = 1500;
const TYPES = [
  { value: 'none', label: 'Texte seul', icon: 'Aa' },
  { value: 'image', label: 'Image + texte', icon: '🖼' },
  { value: 'video', label: 'Vidéo + texte', icon: '▶' },
];

let uidCounter = 0;
const uid = () => `c${++uidCounter}`;
const blankCard = (media_type = 'none', media_url = '') => ({ key: uid(), media_type, media_url, front: '', back: '', uploading: null, error: '' });

function validate(form, cards) {
  const errs = [];
  if (!form.title.trim()) errs.push('Le titre du paquet est obligatoire');
  if (form.published && cards.length === 0) errs.push('Un paquet publié doit contenir au moins une carte');
  cards.forEach((c, i) => {
    if (c.uploading !== null) errs.push(`Carte ${i + 1} : envoi du fichier en cours, patientez`);
    else if (c.media_type === 'image' && !c.media_url) errs.push(`Carte ${i + 1} : ajoutez l'image (ou choisissez « texte seul »)`);
    else if (c.media_type === 'video' && !c.media_url) errs.push(`Carte ${i + 1} : ajoutez la vidéo (ou choisissez « texte seul »)`);
    if (c.media_type === 'none' && !c.front.trim()) errs.push(`Carte ${i + 1} : le texte est vide`);
  });
  return errs;
}

function CardEditor({ card, index, count, onChange, onMove, onRemove, onPickFile }) {
  const fileRef = useRef(null);
  const isText = card.media_type === 'none';
  const pick = () => fileRef.current && fileRef.current.click();

  return (
    <div className="ce-card">
      <div className="ce-card-head">
        <span className="ce-card-num">Carte {index + 1}</span>
        <div className="ce-card-tools">
          <button type="button" className="ce-tool" onClick={() => onMove(-1)} disabled={index === 0} aria-label={`Monter la carte ${index + 1}`}>↑</button>
          <button type="button" className="ce-tool" onClick={() => onMove(1)} disabled={index === count - 1} aria-label={`Descendre la carte ${index + 1}`}>↓</button>
          <button type="button" className="ce-tool danger" onClick={onRemove} aria-label={`Supprimer la carte ${index + 1}`}>✕</button>
        </div>
      </div>

      <div className="ce-types" role="radiogroup" aria-label={`Type de la carte ${index + 1}`}>
        {TYPES.map(t => (
          <button key={t.value} type="button" role="radio" aria-checked={card.media_type === t.value}
            className={card.media_type === t.value ? 'on' : ''}
            onClick={() => onChange({ media_type: t.value, media_url: t.value === card.media_type ? card.media_url : '', error: '' })}>
            <span aria-hidden="true">{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      <div className={`ce-body ${isText ? 'ce-body-text' : ''}`}>
        {!isText && (
          <div className="ce-media">
            <input ref={fileRef} type="file" hidden
              accept={card.media_type === 'image' ? 'image/jpeg,image/png,image/gif,image/webp' : 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v'}
              onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) onPickFile(f); }} />
            {card.uploading !== null ? (
              <div className="ce-media-box ce-uploading">
                <span>envoi… {Math.round(card.uploading * 100)} %</span>
                <span className="ce-upload-bar"><span style={{ transform: `scaleX(${card.uploading})` }} /></span>
              </div>
            ) : card.media_url ? (
              <div className="ce-media-box">
                {card.media_type === 'image'
                  ? <img src={card.media_url} alt="" />
                  : <video src={card.media_url} controls muted playsInline preload="metadata" />}
              </div>
            ) : (
              <button type="button" className="ce-media-box ce-media-empty" onClick={pick}>
                <span className="ce-media-icon" aria-hidden="true">{card.media_type === 'image' ? '🖼' : '▶'}</span>
                {card.media_type === 'image' ? `choisir une image (${IMAGE_MAX_MB} Mo max)` : `choisir une vidéo courte (MP4, WebM, MOV · ${VIDEO_MAX_MB} Mo max)`}
              </button>
            )}
            {card.media_url && card.uploading === null && (
              <button type="button" className="btn secondary ce-change" onClick={pick}>{card.media_type === 'image' ? "changer l'image" : 'changer la vidéo'}</button>
            )}
            {card.error && <p className="ce-error">{card.error}</p>}
          </div>
        )}

        <div className="ce-texts">
          <div className="form-group">
            <label>{isText ? 'Texte de la carte' : 'Texte / question · facultatif'}</label>
            <textarea rows={isText ? 4 : 3} maxLength={MAX_TEXT} value={card.front}
              placeholder={isText ? 'ex : Que dit le théorème de Pythagore ?' : 'ex : Quel est le nom de cette structure ?'}
              onChange={e => onChange({ front: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Réponse au dos · facultatif</label>
            <textarea rows={3} maxLength={MAX_TEXT} value={card.back} placeholder="ex : a² + b² = c² dans un triangle rectangle"
              onChange={e => onChange({ back: e.target.value })} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCardsEdit() {
  const { id } = useParams();
  const isNew = !id;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { authFetch, uploadImage, uploadVideo } = useAuth();

  const [form, setForm] = useState({ title: '', description: '', cover: '', published: true });
  const [cards, setCards] = useState(() => (isNew ? [blankCard()] : []));
  const [loaded, setLoaded] = useState(isNew);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [flash, setFlash] = useState(() => (params.get('cree') ? { type: 'ok', text: '✓ paquet créé' } : null));
  const [bulk, setBulk] = useState(null); // { done, total }
  const [coverUploading, setCoverUploading] = useState(false);
  const bulkRef = useRef(null);
  const coverRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (isNew) return;
    authFetch(`/api/admin/cards/decks/${id}`)
      .then(async r => { if (!r.ok) throw new Error(await errorMessage(r)); return r.json(); })
      .then(d => {
        setForm({ title: d.title, description: d.description || '', cover: d.cover || '', published: d.published });
        setCards(d.cards.map(c => ({ ...blankCard(c.media_type, c.media_url), front: c.front || '', back: c.back || '' })));
        setLoaded(true);
      })
      .catch(e => setFlash({ type: 'error', text: `Chargement impossible : ${e.message}` }));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const setMeta = (patch) => { setForm(prev => ({ ...prev, ...patch })); setDirty(true); };
  const updateCard = (key, patch) => { setCards(cs => cs.map(c => (c.key === key ? { ...c, ...patch } : c))); setDirty(true); };
  const moveCard = (i, dir) => {
    setCards(cs => { const t = i + dir; if (t < 0 || t >= cs.length) return cs; const c = [...cs]; [c[i], c[t]] = [c[t], c[i]]; return c; });
    setDirty(true);
  };
  const removeCard = (card, i) => {
    if ((card.front.trim() || card.back.trim() || card.media_url) && !window.confirm(`Supprimer la carte ${i + 1} ?`)) return;
    setCards(cs => cs.filter(c => c.key !== card.key)); setDirty(true);
  };
  const addCard = (type) => {
    setCards(cs => [...cs, blankCard(type)]); setDirty(true);
    setTimeout(() => bottomRef.current && bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }), 60);
  };

  const sendFile = async (card, file) => {
    const isVideo = card.media_type === 'video';
    const max = isVideo ? VIDEO_MAX_MB : IMAGE_MAX_MB;
    if (file.size > max * 1024 * 1024) { updateCard(card.key, { error: `Fichier trop lourd : ${max} Mo maximum (celui-ci fait ${(file.size / 1048576).toFixed(1)} Mo)` }); return; }
    updateCard(card.key, { uploading: 0, error: '' });
    try {
      const url = isVideo
        ? (await uploadVideo(file, p => updateCard(card.key, { uploading: Math.min(p, 0.99) }))).url
        : await uploadImage(file);
      updateCard(card.key, { media_url: url, uploading: null });
    } catch (e) {
      updateCard(card.key, { uploading: null, error: `Envoi impossible : ${e.message}` });
    }
  };

  // Plusieurs images d'un coup → une carte par image
  const bulkImages = async (files) => {
    const list = [...files].filter(f => /^image\/(jpeg|png|gif|webp)$/.test(f.type));
    if (!list.length) { setFlash({ type: 'error', text: 'Aucune image JPG, PNG, GIF ou WebP sélectionnée' }); return; }
    setBulk({ done: 0, total: list.length });
    const added = []; const failed = [];
    for (const f of list) {
      try {
        if (f.size > IMAGE_MAX_MB * 1024 * 1024) throw new Error('trop lourde');
        added.push(blankCard('image', await uploadImage(f)));
      } catch (e) { failed.push(f.name); }
      setBulk(b => ({ ...b, done: b.done + 1 }));
    }
    setCards(cs => [...cs.filter(c => c.media_type !== 'none' || c.front.trim() || c.back.trim() || c.media_url), ...added]);
    setDirty(true); setBulk(null);
    setFlash(failed.length
      ? { type: 'error', text: `${added.length} carte(s) ajoutée(s) ; non envoyée(s) : ${failed.join(', ')}` }
      : { type: 'ok', text: `✓ ${added.length} carte(s) image ajoutée(s) — ajoutez les textes puis enregistrez` });
  };

  const sendCover = async (file) => {
    setCoverUploading(true);
    try { setMeta({ cover: await uploadImage(file) }); }
    catch (e) { setFlash({ type: 'error', text: `Couverture : ${e.message}` }); }
    finally { setCoverUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    const errs = validate(form, cards);
    setErrors(errs);
    if (errs.length) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSaving(true); setFlash(null);
    const payload = { ...form, cards: cards.map(c => ({ media_type: c.media_type, media_url: c.media_url, front: c.front, back: c.back })) };
    try {
      const r = await authFetch(isNew ? '/api/admin/cards/decks' : `/api/admin/cards/decks/${id}`, { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(payload) });
      if (!r.ok) throw new Error(await errorMessage(r));
      setDirty(false);
      if (isNew) { const d = await r.json(); navigate(`/admin/cards/${d.id}?cree=1`, { replace: true }); return; }
      setFlash({ type: 'ok', text: form.published ? '✓ paquet enregistré — visible sur le site' : '✓ paquet enregistré (brouillon, invisible sur le site)' });
      setTimeout(() => setFlash(null), 2500);
    } catch (err) {
      setFlash({ type: 'error', text: `Non enregistré : ${err.message}` });
    } finally { setSaving(false); }
  };

  if (!loaded) return <div>{flash ? <div className="flash error">{flash.text}</div> : 'chargement…'}</div>;

  const busy = saving || bulk !== null || coverUploading || cards.some(c => c.uploading !== null);

  return (
    <form onSubmit={save}>
      <Link to="/admin/cards" className="qcm-back">← tous les paquets</Link>
      <div className="cv-admin-head">
        <h1 style={{ fontStyle: 'italic' }}>{isNew ? 'Nouveau paquet' : 'Modifier le paquet'}</h1>
        {!isNew && form.published && <a href={`/cards/${id}`} target="_blank" rel="noreferrer" className="btn secondary">voir sur le site ↗</a>}
      </div>

      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}
      {errors.length > 0 && (
        <div className="flash error"><strong>À corriger avant d'enregistrer :</strong><ul style={{ margin: '.4rem 0 0 1.2rem' }}>{errors.map(e => <li key={e}>{e}</li>)}</ul></div>
      )}
      {!form.published && <div className="qcm-draft-banner">Brouillon : ce paquet n'apparaît pas sur le site. Cochez « Publié » pour le rendre visible.</div>}

      <div className="qcm-panel">
        <div className="ce-meta">
          <div className="ce-meta-fields">
            <div className="form-group">
              <label>Titre du paquet</label>
              <input value={form.title} onChange={e => setMeta({ title: e.target.value })} required maxLength={200} placeholder="ex : Anatomie — le squelette" />
            </div>
            <div className="form-group">
              <label>Description (facultatif)</label>
              <input value={form.description} onChange={e => setMeta({ description: e.target.value })} maxLength={500} />
            </div>
            <div className="form-group">
              <label><input type="checkbox" checked={form.published} onChange={e => setMeta({ published: e.target.checked })} /> Publié (visible sur le site)</label>
            </div>
          </div>
          <div className="ce-cover">
            <span className="ce-cover-label">Couverture</span>
            <input ref={coverRef} type="file" hidden accept="image/jpeg,image/png,image/gif,image/webp" onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) sendCover(f); }} />
            <button type="button" className="ce-cover-box" onClick={() => coverRef.current.click()} disabled={coverUploading}>
              {form.cover ? <img src={form.cover} alt="couverture" /> : <span>{coverUploading ? 'envoi…' : '+ image'}</span>}
            </button>
            {form.cover
              ? <button type="button" className="qcm-link-btn" onClick={() => setMeta({ cover: '' })}>retirer</button>
              : <small>par défaut : la 1ʳᵉ image du paquet</small>}
          </div>
        </div>
      </div>

      <div className="ce-list-head">
        <h2>{cards.length} carte{cards.length > 1 ? 's' : ''}</h2>
        <div className="qcm-actions-bar" style={{ margin: 0 }}>
          <input ref={bulkRef} type="file" hidden multiple accept="image/jpeg,image/png,image/gif,image/webp" onChange={e => { const f = e.target.files; if (f.length) bulkImages(f); e.target.value = ''; }} />
          <button type="button" className="btn" onClick={() => addCard('none')}>+ ajouter une carte</button>
          <button type="button" className="btn secondary" onClick={() => bulkRef.current.click()} disabled={bulk !== null}>
            {bulk ? `envoi des images… ${bulk.done}/${bulk.total}` : '⇪ plusieurs images d\'un coup'}
          </button>
        </div>
      </div>

      {cards.length === 0 && <div className="empty-state">aucune carte — ajoutez-en une ci-dessous.</div>}
      {cards.map((c, i) => (
        <CardEditor key={c.key} card={c} index={i} count={cards.length}
          onChange={patch => updateCard(c.key, patch)}
          onMove={dir => moveCard(i, dir)}
          onRemove={() => removeCard(c, i)}
          onPickFile={f => sendFile(c, f)} />
      ))}
      <div ref={bottomRef} />

      <div className="ce-add">
        <span>ajouter une carte :</span>
        {TYPES.map(t => <button key={t.value} type="button" className="btn secondary" onClick={() => addCard(t.value)}><span aria-hidden="true">{t.icon}</span> {t.label}</button>)}
      </div>

      <div className="qcm-savebar">
        <span>{busy && !saving ? 'envoi de fichier en cours…' : dirty ? '● modifications non enregistrées' : '✓ tout est enregistré'}</span>
        <span className="ce-savebar-actions">
        <button type="button" className="btn secondary ce-add-quick" onClick={() => addCard('none')}>+ ajouter une carte</button>
        <button className="btn" type="submit" disabled={busy}>{saving ? 'enregistrement…' : isNew ? 'créer le paquet' : 'enregistrer'}</button>
        </span>
      </div>
    </form>
  );
}
